import { useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { formatMoney, isLive, projectTotals, type Client, type Project } from "@otrack/domain";
import { Button, Icon, IconButton, Segmented, TextInput } from "@otrack/ui";
import { EmptyState, Monogram, PageHeader, rise } from "../components";
import { useApp, useDataset } from "../context";
import { ProjectCard } from "../project-card";
import { href } from "../router";
import { monthRange } from "../time";

type View = "active" | "archived";

export function ProjectsPage() {
  const { now, openNewProject, openNewClient, openImport } = useApp();
  const dataset = useDataset();
  const [view, setView] = useState<View>("active");
  const [query, setQuery] = useState("");
  const month = monthRange(now);

  const clientsById = dataset.clients;
  const liveClients = Object.values(dataset.clients).filter(isLive);
  const liveProjects = Object.values(dataset.projects).filter(isLive);
  const clientArchived = (project: Project) => Boolean(project.clientId && clientsById[project.clientId]?.archivedAt);
  // A Project counts as archived when it is, or when its Client is.
  const inView = (project: Project) => (view === "archived") === (project.archivedAt !== null || clientArchived(project));
  const archivedCount = liveProjects.filter((project) => project.archivedAt !== null || clientArchived(project)).length + liveClients.filter((client) => client.archivedAt !== null && !liveProjects.some((project) => project.clientId === client.id)).length;

  const needle = query.trim().toLowerCase();
  const matches = (project: Project) => {
    if (!needle) return true;
    const client = project.clientId ? clientsById[project.clientId] : undefined;
    return project.name.toLowerCase().includes(needle) || Boolean(client?.name.toLowerCase().includes(needle));
  };

  const projects = liveProjects.filter((project) => inView(project) && matches(project)).sort((a, b) => a.name.localeCompare(b.name));
  const clients = liveClients
    .filter((client) => (view === "archived" ? client.archivedAt !== null || projects.some((project) => project.clientId === client.id) : client.archivedAt === null))
    .filter((client) => !needle || client.name.toLowerCase().includes(needle) || projects.some((project) => project.clientId === client.id))
    .sort((a, b) => a.name.localeCompare(b.name));
  const groups: Array<{ client: Client | null; projects: Project[] }> = [
    ...clients.map((client) => ({ client, projects: projects.filter((project) => project.clientId === client.id) })),
    { client: null, projects: projects.filter((project) => !project.clientId || !clientsById[project.clientId] || !isLive(clientsById[project.clientId]!)) }
  ].filter((group) => group.client || group.projects.length);

  const activeProjects = liveProjects.filter((project) => project.archivedAt === null && !clientArchived(project)).length;
  const activeClients = liveClients.filter((client) => client.archivedAt === null).length;
  const nothingYet = liveProjects.length === 0 && liveClients.length === 0;

  return <div className="ot-page">
    <PageHeader
      title="Projects"
      subtitle={nothingYet ? "Everything you bill for, grouped by who pays." : `${activeProjects} active ${activeProjects === 1 ? "project" : "projects"} for ${activeClients} ${activeClients === 1 ? "client" : "clients"}`}
      actions={<>
        <Button icon="user" onClick={openNewClient}>New client</Button>
        <Button tone="primary" icon="plus" onClick={() => openNewProject()}>New project</Button>
      </>}
    />
    {nothingYet ? <EmptyState
      icon="folder"
      title="No projects yet"
      actions={<><Button tone="primary" icon="plus" onClick={() => openNewProject()}>Create a project</Button><Button icon="download" onClick={openImport}>Import from solidtime</Button></>}
    >Create your first project, or bring your history over from solidtime.</EmptyState> : <>
      <motion.div className="ot-toolbar" {...rise(1)}>
        <TextInput
          className="ot-toolbar__search"
          leading={<Icon name="search"/>}
          trailing={query ? <IconButton className="is-ghost ot-toolbar__clear" label="Clear filter" icon="close" tooltip={false} onClick={() => setQuery("")}/> : undefined}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => { if (event.key === "Escape") setQuery(""); }}
          placeholder="Filter by project or client"
          aria-label="Filter projects"
        />
        {(archivedCount > 0 || view === "archived") && <Segmented
          aria-label="Show"
          value={view}
          onChange={setView}
          options={[{ value: "active", label: "Active" }, { value: "archived", label: "Archived", count: archivedCount }]}
        />}
      </motion.div>
      <div className="ot-groups-frame">
        <div className="ot-groups">
          <AnimatePresence mode="popLayout" initial={false}>
            {groups.length === 0 && <motion.p key="none" className="ot-empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {needle ? <>Nothing matches “{query.trim()}”.</> : view === "archived" ? "Nothing archived." : "Everything here is archived."}
            </motion.p>}
            {groups.map(({ client, projects: list }, groupIndex) => <motion.section
              layout="position"
              className="ot-group"
              key={`${view}:${client?.id ?? "none"}`}
              aria-label={client?.name ?? "No client"}
              style={{ ["--span" as string]: Math.max(list.length, 1) }}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, transition: { duration: 0.15 } }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1], delay: Math.min(groupIndex, 6) * 0.05 }}
            >
              <ClientHead client={client} projects={list} month={month}/>
              {list.length === 0 ? <p className="ot-group__empty">No {view === "archived" ? "archived " : ""}projects for this client yet.</p> : <div className="ot-pgrid">
                <AnimatePresence mode="popLayout">
                  {list.map((project, index) => <ProjectCard key={project.id} project={project} index={index}/>)}
                </AnimatePresence>
              </div>}
            </motion.section>)}
          </AnimatePresence>
        </div>
      </div>
    </>}
  </div>;
}

function ClientHead({ client, projects, month }: { client: Client | null; projects: Project[]; month: { from: number; to: number } }) {
  const { now } = useApp();
  const dataset = useDataset();
  const earned = new Map<string, number>();
  for (const project of projects) {
    const totals = projectTotals(dataset, project.id, now, month);
    if (totals.earned) earned.set(totals.currency, (earned.get(totals.currency) ?? 0) + totals.earned);
  }
  const currency = client ? client.defaultCurrency ?? dataset.settings.defaultCurrency : null;
  const meta = [
    `${projects.length} ${projects.length === 1 ? "project" : "projects"}`,
    client && currency && client.defaultHourlyRate !== null ? `${formatMoney({ amount: client.defaultHourlyRate, currency })}/h` : currency
  ].filter(Boolean).join(" · ");

  return <div className="ot-group__head">
    {client ? <a className="ot-group__client" href={href({ page: "client", id: client.id })}>
      <Monogram name={client.name} picture={client.picture} size={32}/>
      <span className="ot-group__name"><strong>{client.name}</strong><span>{meta}{client.archivedAt ? " · Archived" : ""}</span></span>
      <Icon name="arrow-right" className="ot-group__go"/>
    </a> : <div className="ot-group__client is-static">
      <span className="ot-monogram is-empty" style={{ width: 32, height: 32 }} aria-hidden="true"><Icon name="folder"/></span>
      <span className="ot-group__name"><strong>No client</strong><span>{meta}</span></span>
    </div>}
    {earned.size > 0 && <span className="ot-group__earned">{[...earned].map(([code, amount]) => <span key={code}><strong className="ot-mono">{formatMoney({ amount, currency: code })}</strong> this month</span>)}</span>}
  </div>;
}
